import { useRestApiProvider } from "@jugl-web/rest-api";
import { PageLoaderFull } from "@jugl-web/ui-components/cross-platform";
import { AvatarSelectSize } from "@jugl-web/ui-components/web";
import { useAppVariant, useToast, useTranslations } from "@jugl-web/utils";
import { ResourceMainSettingsItem } from "@web-src/modules/common/components";
import {
  LeaveEntityConfirmationDialogsFlow,
  LeaveEntityConfirmationDialogsFlowHandle,
} from "@web-src/modules/entities/components/LeaveEntityConfirmationDialogsFlow";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { avatarFileSelectConfig } from "@jugl-web/domain-resources/files/consts";
import React, { useCallback, useRef, useState } from "react";
import { WorkspaceEditableName } from "@web-src/modules/workspace/pages/WorkspaceSettingsPage/components/WorkspaceEditableName";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as LeaveIcon } from "./assets/leave.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { SettingsTabsLayout } from "../../components/SettingsTabsLayout/SettingsTabsLayout";
import { WorkspaceOtp } from "./components/WorkspaceOtp";
import { WorkspaceCountry } from "./components/WorkspaceCountry";
import { WorkspaceEmail } from "./components/WorkspaceEmail/WorkspaceEmail";
import { WorkspaceCurrency } from "./components/WorkspaceCurrency";

const WorkspaceSettingsPage: React.FC<{ useDefaultLayout?: boolean }> = ({
  useDefaultLayout = true,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const { entitiesApi } = useRestApiProvider();
  const { entity } = useEntitySelectedProvider();
  const [avatar, setAvatar] = useState<File>();
  const { isCx } = useAppVariant();

  const leaveEntityConfirmationDialogsFlowRef =
    useRef<LeaveEntityConfirmationDialogsFlowHandle | null>(null);

  const [updateEntity, { isLoading }] = entitiesApi.useEditEntityMutation();

  const { localeAwareFormat } = useFormattedDate();

  const { selectFile } = useFileSelect();

  const handleAvatarChange = async (file?: File) => {
    const response = await updateEntity({
      data: {},
      entityId: entity.id,
      displayImg: file,
    });
    if (response && "data" in response) {
      setAvatar(file);
      toast(
        file
          ? t({
              id: "workspace-page.workspace-image-was-updated",
              defaultMessage: "Workspace image was updated",
            })
          : t({
              id: "workspace-page.workspace-image-was-removed",
              defaultMessage: "Workspace image was removed",
            })
      );
    }
  };

  const handleCopyWorkspaceIdToClipboard = useCallback(() => {
    navigator.clipboard.writeText(entity.uniqueId).then(() => {
      toast(
        t({
          id: "workspace-page.workspace-id-copied-to-clipboard",
          defaultMessage: "Workspace ID was copied to clipboard",
        })
      );
    });
  }, [toast, t, entity.uniqueId]);

  const content = (
    <>
      <PageLoaderFull isActive={isLoading} isTransparent />
      <div className="flex flex-col gap-10 p-12">
        <ResourceMainSettingsItem
          avatarSelectProps={{
            onSave: handleAvatarChange,
            size: AvatarSelectSize.xxl,
            value: avatar,
            defaultSrc: entity.logoImg || undefined,
            externalSelectFile: () => selectFile(avatarFileSelectConfig),
          }}
          title={<WorkspaceEditableName />}
          details={[
            {
              title: `${t({
                id: "workspace-page.workspace-id",
                defaultMessage: "Workspace ID",
              })}:`,
              content: entity.uniqueId,
              icon: <CopyIcon />,
              onClick: handleCopyWorkspaceIdToClipboard,
            },
            {
              title: `${t({
                id: "workspace-page.created-on",
                defaultMessage: "Created on",
              })}:`,
              content: localeAwareFormat(
                new Date(entity.__dto.inserted_at),
                "MMM d, uuuu"
              ),
            },
          ]}
          menuItems={[
            {
              id: "leave",
              label: t({
                id: "workspace-page.leave-workspace",
                defaultMessage: "Leave Workspace",
              }),
              icon: <LeaveIcon />,
              onSelect: (_, close) => {
                leaveEntityConfirmationDialogsFlowRef.current?.triggerLeaveEntityFlow(
                  entity
                );
                close();
              },
            },
            {
              id: "delete",
              label: t({
                id: "workspace-page.delete-workspace",
                defaultMessage: "Delete workspace",
              }),
              icon: <DeleteIcon />,
              onSelect: (_, close) => {
                leaveEntityConfirmationDialogsFlowRef.current?.triggerDeleteEntityFlow(
                  entity
                );
                close();
              },
            },
          ]}
        />
        <div className="flex items-center gap-10">
          <WorkspaceCountry />
          <WorkspaceEmail />
          <WorkspaceCurrency />
          {!isCx && <WorkspaceOtp />}
        </div>
      </div>

      <LeaveEntityConfirmationDialogsFlow
        ref={leaveEntityConfirmationDialogsFlowRef}
      />
    </>
  );

  if (!useDefaultLayout) {
    return content;
  }

  return (
    <SettingsTabsLayout selectedTab="settings">{content}</SettingsTabsLayout>
  );
};

export default WorkspaceSettingsPage;
